import { useEffect, useState, useSyncExternalStore } from 'react';

const BEEP_HZ = [880, 660] as const;
const BEEP_MS = 350;
const CYCLE_MS = 1_000;
const GAIN = 0.15;
const VIBRATE_MS = BEEP_MS;
const TOUCH = '(pointer: coarse)';
const UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'] as const;

type AudioContextCtor = typeof AudioContext;

const audioContextCtor = (): AudioContextCtor | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
};

const sharedContexts = new WeakMap<AudioContextCtor, AudioContext>();

const sharedContextOf = (Ctor: AudioContextCtor): AudioContext => {
  const existing = sharedContexts.get(Ctor);
  if (existing !== undefined && existing.state !== 'closed') return existing;
  const made = new Ctor();
  sharedContexts.set(Ctor, made);
  return made;
};

const isTouchScreen = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia(TOUCH).matches;

const vibrate = (pattern: number): void => {
  navigator.vibrate?.(pattern);
};

const beep = (context: AudioContext, hz: number): void => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = hz;
  gain.gain.value = GAIN;
  oscillator.connect(gain).connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + BEEP_MS / 1000);
};

type AudioStatus = 'idle' | 'playing' | 'held' | 'muted' | 'unavailable';

const statusOf = (context: AudioContext | null): AudioStatus => {
  if (context === null) return 'unavailable';
  return context.state === 'running' ? 'playing' : 'held';
};

const restingStatus = (): AudioStatus => (audioContextCtor() === null ? 'unavailable' : 'idle');

interface AlarmEngine {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => AudioStatus;
  readonly start: () => void;
  readonly stop: () => void;
  readonly mute: () => void;
}

const createAlarmEngine = (): AlarmEngine => {
  const listeners = new Set<() => void>();
  let status: AudioStatus = restingStatus();
  let teardown: (() => void) | null = null;
  let silence: (() => void) | null = null;

  const set = (next: AudioStatus): void => {
    if (next === status) return;
    status = next;
    listeners.forEach((listener) => listener());
  };

  const start = (): void => {
    if (teardown !== null) return;
    const Ctor = audioContextCtor();
    const context = Ctor === null ? null : sharedContextOf(Ctor);
    const touch = isTouchScreen();
    const readStatus = () => {
      if (status !== 'muted') set(statusOf(context));
    };
    const unlock = () => {
      if (context !== null && context.state !== 'running') void context.resume().catch(() => undefined);
    };
    context?.addEventListener('statechange', readStatus);
    UNLOCK_EVENTS.forEach((type) => window.addEventListener(type, unlock));
    let turn = 0;
    const ring = () => {
      if (touch) vibrate(VIBRATE_MS);
      if (context?.state !== 'running') return;
      beep(context, BEEP_HZ[turn % BEEP_HZ.length]);
      turn += 1;
    };
    unlock();
    readStatus();
    ring();
    let timer: number | null = window.setInterval(ring, CYCLE_MS);
    const quiet = () => {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
      if (touch) vibrate(0);
    };
    silence = () => {
      quiet();
      set('muted');
    };
    teardown = () => {
      quiet();
      UNLOCK_EVENTS.forEach((type) => window.removeEventListener(type, unlock));
      context?.removeEventListener('statechange', readStatus);
    };
  };

  const stop = (): void => {
    teardown?.();
    teardown = null;
    silence = null;
    set(restingStatus());
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => status,
    start,
    stop,
    mute: () => silence?.(),
  };
};

export type AlarmState = 'ringing' | 'blocked' | 'muted' | 'unavailable' | 'off';

export interface AlarmTone {
  readonly state: AlarmState;
  readonly mute: () => void;
}

const stateOf = (active: boolean, audio: AudioStatus): AlarmState => {
  if (!active) return 'off';
  switch (audio) {
    case 'unavailable':
      return 'unavailable';
    case 'muted':
      return 'muted';
    case 'held':
      return 'blocked';
    case 'idle':
    case 'playing':
      return 'ringing';
  }
};

export const useAlarmTone = (active: boolean): AlarmTone => {
  const [engine] = useState(createAlarmEngine);
  const audio = useSyncExternalStore(engine.subscribe, engine.getSnapshot, engine.getSnapshot);

  useEffect(() => {
    if (!active) return undefined;
    engine.start();
    return engine.stop;
  }, [active, engine]);

  return { state: stateOf(active, audio), mute: engine.mute };
};
